import { makeAutoObservable, runInAction } from 'mobx';
import { fabric } from 'fabric';
import { nanoid } from 'nanoid';

export interface ElementProxy {
  type: string;
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  src?: string;
  text?: string;
  fontSize?: number;
  fill?: string;
  selectable: boolean;
  opacity: number;
  angle?: number;
  showInExport?: boolean;
  extensionType?: string;
  fabricObject?: fabric.Object;
  set(props: Partial<ElementProxy> & Record<string, any>): void;
}

export interface PageProxy {
  id: string;
  children: ElementProxy[];
  computedWidth: number;
  computedHeight: number;
  addElement(el: Record<string, any>): any;
}

interface PageSnapshot {
  id: string;
  fabricJSON: any | null;
  polotnoPage?: any;
}

type ChangeListener = () => void;

const CUSTOM_PROPS = [
  'id', 'customType', 'showInExport', 'selectable', 'evented',
  'lockMovementX', 'lockMovementY', 'lockScalingX', 'lockScalingY',
  'lockRotation', 'hasControls', 'hasBorders', 'extensionType', 'isPlaceholder',
  'frameId', 'frameLeft', 'frameTop', 'frameWidth', 'frameHeight',
];

/** Clamps a frame image's position on one axis: if it's at least as large as the frame it must cover
 *  the frame (no gaps at edges); if it's smaller (zoomed out past cover) it's centred in the frame. */
function clampToFrame(pos: number, size: number, frameStart: number, frameSize: number): number {
  if (size <= frameSize) return frameStart + (frameSize - size) / 2;
  return Math.min(frameStart, Math.max(frameStart + frameSize - size, pos));
}

class EditorStore {
  private _canvas: fabric.Canvas | null = null;
  private _container: HTMLElement | null = null;
  private _pages: PageSnapshot[] = [];
  private _activePageIndex = 0;
  private _changeListeners: Set<ChangeListener> = new Set();
  private _historyStack: string[] = [];
  private _historyIndex = -1;
  private readonly _maxHistory = 50;
  private _suppressHistory = false;
  private _thumbnailCache: Record<string, string> = {};
  private _isPanning = false;
  private _lastPanX = 0;
  private _lastPanY = 0;
  // Persists across side-panel clicks so placeImageFromPanel still has a target frame
  private _lastSelectedFrame: fabric.Object | null = null;
  private _historyTimer: ReturnType<typeof setTimeout> | null = null;
  private _thumbTimer: ReturnType<typeof setTimeout> | null = null;

  // MobX observable state
  width = 0;
  height = 0;
  activePageIndex = 0;
  scale = 1;
  selectedElements: ElementProxy[] = [];
  pageIds: string[] = [];
  thumbnails: Record<string, string> = {};
  layersRevision = 0;

  constructor() {
    makeAutoObservable(this);
  }

  // ─── Canvas lifecycle ────────────────────────────────────────────────────────

  /** Loads JSON into the main canvas. Fabric clears the canvas internally once images finish loading,
   *  which crashes if the canvas was disposed meanwhile (e.g. logout mid-load) — so loads are counted
   *  and _disposeCanvas waits for them. `done` is skipped if the canvas was replaced or destroyed. */
  private _loadIntoCanvas(json: any, done: () => void) {
    const canvas = this._canvas;
    if (!canvas) return;
    const c = canvas as any;
    const seq = this._startPageLoad(canvas, false);
    c.__jsonLoadSeq = seq;
    c.__pendingLoads = (c.__pendingLoads || 0) + 1;
    canvas.loadFromJSON(json, () => {
      c.__pendingLoads--;
      if (c.__disposeWhenIdle) {
        if (c.__pendingLoads === 0) { try { canvas.dispose(); } catch (_) {} }
        return;
      }
      if (canvas !== this._canvas) return;
      if (seq !== c.__loadSeq) {
        // A newer page load/switch started meanwhile, and Fabric has just drawn this stale page over it.
        // Only a newer loadFromJSON that's still in flight will clear and redraw by itself; in every other
        // case (it already finished, or it was a synchronous clear such as a legacy Polotno page) redraw now.
        const newerJsonLoadPending = !c.__pageLoaded && c.__jsonLoadSeq === c.__loadSeq;
        if (!newerJsonLoadPending) this._loadActivePage();
        return;
      }
      c.__pageLoaded = true;
      done();
    });
  }

  /** Marks a new page as being shown on the canvas; any load still in flight becomes stale.
   *  `loaded` is false while the page's content is still arriving, so it isn't serialized half-done. */
  private _startPageLoad(canvas: fabric.Canvas, loaded: boolean): number {
    const c = canvas as any;
    c.__loadSeq = (c.__loadSeq || 0) + 1;
    c.__pageLoaded = loaded;
    return c.__loadSeq;
  }

  private _disposeCanvas(canvas: fabric.Canvas) {
    const c = canvas as any;
    if (c.__pendingLoads > 0) { c.__disposeWhenIdle = true; return; }
    try { canvas.dispose(); } catch (_) {}
  }

  initCanvas(el: HTMLCanvasElement, container?: HTMLElement) {
    if (this._canvas) {
      this._disposeCanvas(this._canvas);
    }
    this._container = container || el.parentElement;
    this._canvas = new fabric.Canvas(el, {
      preserveObjectStacking: true,
      backgroundColor: '#e5e7eb', // grey workspace; white page rect is drawn via _setPageBackground
    });

    // Size canvas element to container
    if (this._container) {
      this._canvas.setWidth(this._container.clientWidth || 800);
      this._canvas.setHeight(this._container.clientHeight || 600);
    }

    this._canvas.on('object:modified', this._onChanged);
    this._canvas.on('object:added', this._onChanged);
    this._canvas.on('object:removed', this._onChanged);
    this._canvas.on('selection:created', this._onSelectionChanged);
    this._canvas.on('selection:updated', this._onSelectionChanged);
    this._canvas.on('selection:cleared', this._onSelectionCleared);

    // Track the last selected frame so side-panel clicks don't lose the target.
    // Intentionally NOT cleared on selection:cleared — we want it to survive the
    // brief deselection that happens when the user clicks in the upload panel.
    const trackFrame = () => {
      const obj = this._canvas?.getActiveObject();
      if (obj) {
        const ct = (obj as any).customType;
        if (ct === 'photoFrame' || ct === 'frameImage') {
          this._lastSelectedFrame = obj;
        }
      }
    };
    this._canvas.on('selection:created', trackFrame);
    this._canvas.on('selection:updated', trackFrame);

    // Alt + drag → pan
    this._canvas.on('mouse:down', (opt) => {
      const e = opt.e as MouseEvent;
      if (e.altKey) {
        this._isPanning = true;
        this._lastPanX = e.clientX;
        this._lastPanY = e.clientY;
        this._canvas!.defaultCursor = 'grabbing';
        this._canvas!.setCursor('grabbing');
        this._canvas!.selection = false;
      }
    });
    this._canvas.on('mouse:move', (opt) => {
      if (!this._isPanning || !opt.e) return;
      const e = opt.e as MouseEvent;
      const dx = e.clientX - this._lastPanX;
      const dy = e.clientY - this._lastPanY;
      this._canvas!.relativePan(new fabric.Point(dx, dy));
      this._lastPanX = e.clientX;
      this._lastPanY = e.clientY;
    });
    this._canvas.on('mouse:up', () => {
      if (this._isPanning) {
        this._isPanning = false;
        this._canvas!.defaultCursor = 'default';
        this._canvas!.selection = true;
      }
    });

    if (this._pages.length > 0) {
      this._loadActivePage();
    } else {
      requestAnimationFrame(() => this.fitCanvasToContainer());
    }
  }

  destroy() {
    // Cancel in-flight timers so stale callbacks don't fire after disposal
    if (this._historyTimer) { clearTimeout(this._historyTimer); this._historyTimer = null; }
    if (this._thumbTimer) { clearTimeout(this._thumbTimer); this._thumbTimer = null; }

    if (this._canvas) {
      this._disposeCanvas(this._canvas);
      this._canvas = null;
    }
    this._lastSelectedFrame = null;

    // CRITICAL: clear all external change listeners (e.g. Photobook/Canvas auto-saves).
    // Without this, a listener registered by Editor A survives into Editor B's session.
    // React unmounts children before parents, so PhotobookWorkspace.destroy() runs
    // BEFORE PhotobookEditor's auto-save cleanup — meaning the listener is still alive
    // when the Canvas editor starts firing change events, which causes Canvas data to be
    // saved to the Photobook backend record.
    this._changeListeners.clear();

    // Clear ALL state so the next initCanvas() starts with a blank slate.
    this._pages = [];
    this._activePageIndex = 0;
    this._historyStack = [];
    this._historyIndex = -1;
    this._thumbnailCache = {};
    this._container = null;

    runInAction(() => {
      this.width = 0;
      this.height = 0;
      this.activePageIndex = 0;
      this.scale = 1;
      this.pageIds = [];
      this.thumbnails = {};
      this.selectedElements = [];
      this.layersRevision++;
    });
  }

  // ─── Zoom / pan / fit ────────────────────────────────────────────────────────

  fitCanvasToContainer() {
    if (!this._canvas || !this._container || !this.width || !this.height) return;
    const cw = this._container.clientWidth;
    const ch = this._container.clientHeight;
    if (!cw || !ch) return;

    this._canvas.setWidth(cw);
    this._canvas.setHeight(ch);

    const padding = 40;
    const zoom = Math.min((cw - padding * 2) / this.width, (ch - padding * 2) / this.height);
    const panX = (cw - this.width * zoom) / 2;
    const panY = (ch - this.height * zoom) / 2;

    this._canvas.setViewportTransform([zoom, 0, 0, zoom, panX, panY]);
    runInAction(() => { this.scale = zoom; });
    this._canvas.requestRenderAll();
  }

  /** True if a container-relative screen point lies over the page (canvas coords 0..width, 0..height). */
  isScreenPointOnPage(screenX: number, screenY: number) {
    if (!this._canvas) return false;
    const vt = this._canvas.viewportTransform || [1, 0, 0, 1, 0, 0];
    const x = (screenX - vt[4]) / vt[0];
    const y = (screenY - vt[5]) / vt[3];
    return x >= 0 && y >= 0 && x <= this.width && y <= this.height;
  }

  zoomAtPoint(screenX: number, screenY: number, delta: number) {
    if (!this._canvas) return;
    const factor = delta < 0 ? 1.08 : 1 / 1.08;
    const newZoom = Math.min(Math.max(this._canvas.getZoom() * factor, 0.05), 20);
    this._canvas.zoomToPoint(new fabric.Point(screenX, screenY), newZoom);
    runInAction(() => { this.scale = this._canvas!.getZoom(); });
  }

  zoom(factor: number) {
    if (!this._canvas || !this._container) return;
    const cx = this._container.clientWidth / 2;
    const cy = this._container.clientHeight / 2;
    const newZoom = Math.min(Math.max(this._canvas.getZoom() * factor, 0.05), 20);
    this._canvas.zoomToPoint(new fabric.Point(cx, cy), newZoom);
    runInAction(() => { this.scale = this._canvas!.getZoom(); });
  }

  /** Zooms a placed frame image in/out within its clip boundary.
   *  Minimum scale = cover scale so the frame always stays fully covered. */
  zoomFrameImage(frameImg: fabric.Object, delta: number) {
    if (!this._canvas) return;
    const frameLeft: number = (frameImg as any).frameLeft ?? 0;
    const frameTop: number  = (frameImg as any).frameTop  ?? 0;
    const frameW: number    = (frameImg as any).frameWidth  ?? 0;
    const frameH: number    = (frameImg as any).frameHeight ?? 0;
    const imgW = frameImg.width || 1;
    const imgH = frameImg.height || 1;

    const factor = delta < 0 ? 1.1 : 1 / 1.1;
    const minScale = Math.min(frameW / imgW, frameH / imgH); // contain = minimum allowed (whole photo visible)
    const currentScale = frameImg.scaleX || 1;
    const newScale = Math.max(minScale, currentScale * factor);

    const currentLeft = frameImg.left ?? 0;
    const currentTop  = frameImg.top  ?? 0;

    // Scale around the frame centre so the visible area stays centred
    const frameCX = frameLeft + frameW / 2;
    const frameCY = frameTop  + frameH / 2;
    const ratio = newScale / currentScale;
    const newLeft = frameCX - (frameCX - currentLeft) * ratio;
    const newTop  = frameCY - (frameCY - currentTop)  * ratio;

    // Re-clamp after zoom so image still covers frame (or stays centred on an axis where it's smaller)
    const sw = imgW * newScale;
    const sh = imgH * newScale;
    const clampedLeft = clampToFrame(newLeft, sw, frameLeft, frameW);
    const clampedTop  = clampToFrame(newTop,  sh, frameTop,  frameH);

    frameImg.set({ scaleX: newScale, scaleY: newScale, left: clampedLeft, top: clampedTop } as any);
    this._canvas.requestRenderAll();
    this._onChanged();
  }

  // ─── Layer management ────────────────────────────────────────────────────────

  bringForward() {
    const obj = this._canvas?.getActiveObject();
    if (obj) { this._canvas?.bringForward(obj, false); this._onChanged(); }
  }
  sendBackward() {
    const obj = this._canvas?.getActiveObject();
    if (obj) { this._canvas?.sendBackwards(obj, false); this._keepPageBgAtBottom(); this._onChanged(); }
  }
  bringToFront() {
    const obj = this._canvas?.getActiveObject();
    if (obj) { this._canvas?.bringToFront(obj); this._onChanged(); }
  }
  sendToBack() {
    const obj = this._canvas?.getActiveObject();
    if (obj) { this._canvas?.sendToBack(obj); this._keepPageBgAtBottom(); this._onChanged(); }
  }

  /** Restores the white page rect (index 0) and border (index 1) below user objects after a reorder. */
  private _keepPageBgAtBottom() {
    const canvas = this._canvas;
    if (!canvas) return;
    const objects = canvas.getObjects();
    const border = objects.find(o => (o as any).id === '_page_border');
    const bg = objects.find(o => (o as any).id === '_page_bg');
    if (border) canvas.sendToBack(border);
    if (bg) canvas.sendToBack(bg);
  }

  deleteSelected() {
    const canvas = this._canvas;
    if (!canvas) return;
    const targets = canvas.getActiveObjects();
    if (!targets.length) return;
    canvas.discardActiveObject();
    targets.forEach(o => canvas.remove(o));
    canvas.requestRenderAll();
    this._onChanged();
  }

  selectObjectById(id: string) {
    const obj = this._canvas?.getObjects().find(o => (o as any).id === id);
    if (obj && this._canvas) {
      this._canvas.discardActiveObject();
      this._canvas.setActiveObject(obj);
      this._canvas.requestRenderAll();
    }
  }

  get layers(): ElementProxy[] {
    void this.layersRevision;
    return [...(this._canvas?.getObjects() ?? [])]
      .filter(o => (o as any).id !== '_page_bg' && (o as any).id !== '_page_border')
      .reverse()
      .map(o => this._makeProxy(o));
  }

  // ─── Canvas state ────────────────────────────────────────────────────────────

  get activePage(): PageProxy {
    const self = this;
    const pageId = this._pages[this._activePageIndex]?.id ?? '';
    return {
      id: pageId,
      get children(): ElementProxy[] {
        return (self._canvas?.getObjects() ?? []).map(o => self._makeProxy(o));
      },
      get computedWidth() { return self.width; },
      get computedHeight() { return self.height; },
      addElement(el: any) { return self._addElement(el); },
    };
  }

  get activePageId(): string {
    return this._pages[this._activePageIndex]?.id ?? '';
  }

  get pages(): PageProxy[] {
    const self = this;
    return this._pages.map((p, idx) => ({
      id: p.id,
      get children(): ElementProxy[] {
        if (idx === self._activePageIndex) {
          return (self._canvas?.getObjects() ?? []).map(o => self._makeProxy(o));
        }
        return ((p.fabricJSON?.objects as any[]) ?? []).map((o: any) => ({
          type: o.customType || o.type || 'unknown',
          id: o.id || '',
          x: o.left ?? 0,
          y: o.top ?? 0,
          width: (o.width || 0) * (o.scaleX || 1),
          height: (o.height || 0) * (o.scaleY || 1),
          selectable: o.selectable !== false,
          opacity: o.opacity ?? 1,
          showInExport: o.showInExport,
          extensionType: o.extensionType,
          set: () => {},
        } as ElementProxy));
      },
      computedWidth: self.width,
      computedHeight: self.height,
      addElement(el: any) {
        if (idx !== self._activePageIndex) self.selectPage(p.id);
        return self._addElement(el);
      },
    }));
  }

  setSize(w: number, h: number) {
    runInAction(() => { this.width = w; this.height = h; });
    this._setPageBackground();
    requestAnimationFrame(() => this.fitCanvasToContainer());
  }

  clear() {
    this._pages = [];
    this._activePageIndex = 0;
    this._historyStack = [];
    this._historyIndex = -1;
    this._thumbnailCache = {};
    this._lastSelectedFrame = null;
    if (this._canvas) {
      this._suppressHistory = true;
      this._startPageLoad(this._canvas, true);
      this._canvas.clear();
      this._canvas.backgroundColor = '#ffffff';
      this._canvas.requestRenderAll();
      this._suppressHistory = false;
    }
    runInAction(() => {
      this.activePageIndex = 0;
      this.pageIds = [];
      this.thumbnails = {};
      this.selectedElements = [];
      this.layersRevision++;
    });
  }

  addPage(): PageProxy {
    if (this._pages.length > 0) this._serializeActivePage();
    const newPage: PageSnapshot = {
      id: nanoid(10),
      fabricJSON: { version: '5.3.0', objects: [], background: '#ffffff' },
    };
    this._pages.push(newPage);
    this._activePageIndex = this._pages.length - 1;
    this._lastSelectedFrame = null;

    if (this._canvas) {
      this._suppressHistory = true;
      this._startPageLoad(this._canvas, true);
      this._canvas.clear();
      this._suppressHistory = false;
      this._setPageBackground();
      this._canvas.requestRenderAll();
    }
    runInAction(() => {
      this.activePageIndex = this._activePageIndex;
      this.pageIds = this._pages.map(p => p.id);
      this.layersRevision++;
    });
    return this.activePage;
  }

  selectPage(pageId: string) {
    const idx = this._pages.findIndex(p => p.id === pageId);
    if (idx === -1 || idx === this._activePageIndex) return;
    this._serializeActivePage();
    this._activePageIndex = idx;
    this._lastSelectedFrame = null;
    runInAction(() => { this.activePageIndex = idx; });
    this._loadActivePage();
  }

  loadJSON(json: any) {
    const normalized = this._normalizeJSON(json);
    this._pages = normalized.pages;
    this._activePageIndex = 0;
    this._lastSelectedFrame = null;
    this._thumbnailCache = {};
    this._historyStack = [];
    this._historyIndex = -1;
    runInAction(() => {
      this.width = normalized.width || this.width;
      this.height = normalized.height || this.height;
      this.activePageIndex = 0;
      this.pageIds = this._pages.map(p => p.id);
      this.thumbnails = {};
      this.layersRevision++;
    });
    this._loadActivePage();
    // Generate thumbnails for all saved pages so the thumbnail strip shows content
    setTimeout(() => this._generateAllThumbnails(), 800);
  }

  toJSON(): any {
    this._serializeActivePage();
    return {
      _editorVersion: 1,
      width: this.width,
      height: this.height,
      // Legacy Polotno pages that were never opened have no fabricJSON yet — keep their original data
      pages: this._pages.map(p => (p.fabricJSON || !p.polotnoPage)
        ? { id: p.id, fabricJSON: p.fabricJSON }
        : { id: p.id, fabricJSON: null, polotnoPage: p.polotnoPage }),
    };
  }

  async toDataURL(opts?: { pageId?: string }): Promise<string> {
    if (!this._canvas) return '';

    const renderAtFullRes = (c: fabric.Canvas, w: number, h: number): string => {
      const savedVT = [...(c.viewportTransform || [1, 0, 0, 1, 0, 0])];
      const savedW = c.getWidth();
      const savedH = c.getHeight();
      c.setViewportTransform([1, 0, 0, 1, 0, 0]);
      c.setWidth(w);
      c.setHeight(h);
      const toHide: fabric.Object[] = [];
      c.getObjects().forEach(o => {
        if ((o as any).showInExport === false) { (o as any).visible = false; toHide.push(o); }
      });
      c.renderAll();
      const url = c.toDataURL({ format: 'jpeg', quality: 0.92 });
      toHide.forEach(o => { (o as any).visible = true; });
      c.setViewportTransform(savedVT as any);
      c.setWidth(savedW);
      c.setHeight(savedH);
      c.requestRenderAll();
      return url;
    };

    if (opts?.pageId && opts.pageId !== this.activePageId) {
      this._serializeActivePage();
      const targetIdx = this._pages.findIndex(p => p.id === opts.pageId);
      if (targetIdx === -1) return '';
      return new Promise<string>(resolve => {
        const offEl = document.createElement('canvas');
        const temp = new fabric.Canvas(offEl, { enableRetinaScaling: false });
        temp.setWidth(this.width);
        temp.setHeight(this.height);
        temp.loadFromJSON(this._pages[targetIdx].fabricJSON || {}, () => {
          (temp as any).backgroundColor = '#ffffff';
          const toHide: fabric.Object[] = [];
          temp.getObjects().forEach(o => {
            if ((o as any).showInExport === false) { (o as any).visible = false; toHide.push(o); }
          });
          temp.renderAll();
          const url = temp.toDataURL({ format: 'jpeg', quality: 0.92 });
          toHide.forEach(o => { (o as any).visible = true; });
          temp.dispose();
          resolve(url);
        });
      });
    }

    return renderAtFullRes(this._canvas, this.width, this.height);
  }

  on(_event: 'change', handler: ChangeListener): () => void {
    this._changeListeners.add(handler);
    return () => this._changeListeners.delete(handler);
  }

  waitLoading(): Promise<void> {
    return Promise.resolve();
  }

  /** Call after direct property edits (toolbar) to push history + update MobX observers. */
  refreshAfterEdit() {
    runInAction(() => { this.layersRevision++; });
    this._debouncedPushHistory();
    this._scheduleThumbnailUpdate();
    this._changeListeners.forEach(fn => fn());
  }

  getCanvas(): fabric.Canvas | null {
    return this._canvas;
  }

  // ─── Photo frame / template system ─────────────────────────────────────────

  /** Returns the topmost frame (placeholder or filled) under the given canvas-space point. */
  getFrameAtPoint(canvasX: number, canvasY: number): fabric.Object | null {
    const objects = this._canvas?.getObjects() ?? [];
    for (let i = objects.length - 1; i >= 0; i--) {
      const obj = objects[i];
      const ct = (obj as any).customType;
      if (ct === 'photoFrame') {
        if (obj.containsPoint(new fabric.Point(canvasX, canvasY))) return obj;
      }
      if (ct === 'frameImage') {
        // Use stored frame bounds (not image bounds, which can be larger due to cover-scaling)
        const fl = (obj as any).frameLeft ?? 0;
        const ft = (obj as any).frameTop ?? 0;
        const fw = (obj as any).frameWidth ?? 0;
        const fh = (obj as any).frameHeight ?? 0;
        if (canvasX >= fl && canvasX <= fl + fw && canvasY >= ft && canvasY <= ft + fh) return obj;
      }
    }
    return null;
  }

  /** Returns the currently selected frame object (placeholder or filled image), or null. */
  getActiveFrame(): fabric.Object | null {
    const active = this._canvas?.getActiveObject();
    if (!active) return null;
    const ct = (active as any).customType;
    return (ct === 'photoFrame' || ct === 'frameImage') ? active : null;
  }

  /**
   * Returns the last frame the user clicked on the canvas.
   * Survives side-panel clicks that momentarily clear canvas selection.
   */
  getLastSelectedFrame(): fabric.Object | null {
    return this._lastSelectedFrame;
  }

  /**
   * Returns the first empty photoFrame placeholder on the current page, or null.
   * Used as a final fallback when no frame is active or remembered.
   */
  getFirstEmptyPhotoFrame(): fabric.Object | null {
    const objects = this._canvas?.getObjects() ?? [];
    return objects.find(o => (o as any).customType === 'photoFrame') ?? null;
  }

  /** Clears the remembered last-selected frame (call on page switch). */
  clearLastSelectedFrame(): void {
    this._lastSelectedFrame = null;
  }

  /** Adds photo frame placeholder objects for a dropped template. */
  addPhotoFrames(frames: Array<{ x: number; y: number; w: number; h: number }>) {
    if (!this._canvas) return;
    const canvas = this._canvas;
    const cw = this.width;
    const ch = this.height;

    frames.forEach(f => {
      const px = f.x * cw;
      const py = f.y * ch;
      const pw = f.w * cw;
      const ph = f.h * ch;
      const frameId = nanoid(6);

      const rect = new fabric.Rect({
        left: px, top: py, width: pw, height: ph,
        fill: '#f3f4f6',
        stroke: '#9ca3af', strokeWidth: 2, strokeDashArray: [8, 5],
        selectable: true, evented: true,
        rx: 4, ry: 4,
      } as any);
      (rect as any).customType = 'photoFrame';
      (rect as any).isPlaceholder = true;
      (rect as any).frameId = frameId;
      (rect as any).showInExport = false; // hidden in preview/export — only visible during editing

      // Placeholder label centered in the frame
      const label = new fabric.Text('＋ Drop photo here', {
        left: px + pw / 2,
        top: py + ph / 2,
        fontSize: Math.max(12, Math.min(18, ph * 0.06)),
        fill: '#9ca3af',
        textAlign: 'center',
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
      } as any);
      (label as any).customType = 'photoFrameLabel';
      (label as any).frameId = frameId;
      (label as any).showInExport = false; // hidden in preview/export

      canvas.add(rect);
      canvas.add(label);
    });

    canvas.requestRenderAll();
    this._onChanged();
  }

  /** Places an image inside a frame with CSS cover-fit (fill, crop excess, center).
   *  Works for both empty placeholder frames and frames that already contain an image. */
  placeImageInFrame(src: string, frameObj: fabric.Object) {
    const canvas = this._canvas;
    if (!canvas) return;

    const ct = (frameObj as any).customType;
    const frameId: string = (frameObj as any).frameId;

    // Recover frame bounds — for existing frameImages use stored props; for placeholders use geometry
    const frameLeft: number = ct === 'frameImage'
      ? ((frameObj as any).frameLeft ?? frameObj.left ?? 0)
      : (frameObj.left ?? 0);
    const frameTop: number = ct === 'frameImage'
      ? ((frameObj as any).frameTop ?? frameObj.top ?? 0)
      : (frameObj.top ?? 0);
    const frameW: number = ct === 'frameImage'
      ? ((frameObj as any).frameWidth ?? frameObj.getScaledWidth())
      : frameObj.getScaledWidth();
    const frameH: number = ct === 'frameImage'
      ? ((frameObj as any).frameHeight ?? frameObj.getScaledHeight())
      : frameObj.getScaledHeight();

    fabric.Image.fromURL(src, (img) => {
      // Cover scale: image fills the frame completely, excess is cropped
      const scale = Math.max(frameW / (img.width || 1), frameH / (img.height || 1));
      const scaledW = (img.width || 1) * scale;
      const scaledH = (img.height || 1) * scale;

      // absolutePositioned ensures clip uses canvas coordinates, not object-relative
      const clipRect = new fabric.Rect({
        left: frameLeft, top: frameTop,
        width: frameW, height: frameH,
        absolutePositioned: true,
      });

      img.set({
        left: frameLeft + (frameW - scaledW) / 2,   // centered, left-origin
        top: frameTop + (frameH - scaledH) / 2,
        originX: 'left', originY: 'top',
        scaleX: scale, scaleY: scale,
        clipPath: clipRect,
        selectable: true, evented: true,
      } as any);

      // Tag the image so we can find/replace it later
      (img as any).customType = 'frameImage';
      (img as any).frameId = frameId;
      (img as any).frameLeft = frameLeft;
      (img as any).frameTop = frameTop;
      (img as any).frameWidth = frameW;
      (img as any).frameHeight = frameH;
      (img as any).id = nanoid(6);

      // Clamp movement so image always covers the frame (no gaps at edges)
      img.on('moving', () => {
        const sw = (img.width || 1) * (img.scaleX || 1);
        const sh = (img.height || 1) * (img.scaleY || 1);
        const clampedLeft = clampToFrame(img.left ?? 0, sw, frameLeft, frameW);
        const clampedTop  = clampToFrame(img.top  ?? 0, sh, frameTop,  frameH);
        img.set({ left: clampedLeft, top: clampedTop } as any);
      });

      // Remove ALL objects tied to this frame (placeholder, label, previous image)
      const toRemove = canvas.getObjects().filter(o => {
        const oFid = (o as any).frameId;
        const oCt = (o as any).customType;
        return oFid === frameId && (
          oCt === 'photoFrame' || oCt === 'photoFrameLabel' || oCt === 'frameImage'
        );
      });
      toRemove.forEach(o => canvas.remove(o));

      canvas.add(img);
      canvas.setActiveObject(img);
      canvas.requestRenderAll();
      this._onChanged();
    }, { crossOrigin: 'anonymous' });
  }

  history = {
    undo: () => {
      if (this._historyIndex <= 0) return;
      this._historyIndex--;
      this._suppressHistory = true;
      this._loadIntoCanvas(JSON.parse(this._historyStack[this._historyIndex]), () => {
        this._setPageBackground();
        this._canvas?.requestRenderAll();
        this._suppressHistory = false;
        this._scheduleThumbnailUpdate();
        runInAction(() => { this.layersRevision++; });
        this._changeListeners.forEach(fn => fn());
      });
    },
    redo: () => {
      if (this._historyIndex >= this._historyStack.length - 1) return;
      this._historyIndex++;
      this._suppressHistory = true;
      this._loadIntoCanvas(JSON.parse(this._historyStack[this._historyIndex]), () => {
        this._setPageBackground();
        this._canvas?.requestRenderAll();
        this._suppressHistory = false;
        this._scheduleThumbnailUpdate();
        runInAction(() => { this.layersRevision++; });
        this._changeListeners.forEach(fn => fn());
      });
    },
  };

  // ─── Private helpers ─────────────────────────────────────────────────────────

  private _onChanged = () => {
    if (this._suppressHistory) return;
    this._debouncedPushHistory();
    this._scheduleThumbnailUpdate();
    this._changeListeners.forEach(fn => fn());
    runInAction(() => { this.layersRevision++; });
  };

  private _onSelectionChanged = () => {
    const objects = this._canvas?.getActiveObjects() || [];
    runInAction(() => {
      this.selectedElements = objects.map(o => this._makeProxy(o));
    });
  };

  private _onSelectionCleared = () => {
    runInAction(() => { this.selectedElements = []; });
  };

  private _makeProxy(obj: fabric.Object): ElementProxy {
    const self = this;
    return {
      type: (obj as any).customType || obj.type || 'unknown',
      id: (obj as any).id || '',
      x: obj.left ?? 0,
      y: obj.top ?? 0,
      width: obj.getScaledWidth(),
      height: obj.getScaledHeight(),
      src: (obj as any).getSrc?.() ?? '',
      text: (obj as any).text ?? '',
      fontSize: (obj as any).fontSize ?? 0,
      fill: String((obj as any).fill ?? '#000000'),
      selectable: obj.selectable !== false,
      opacity: obj.opacity ?? 1,
      angle: obj.angle ?? 0,
      showInExport: (obj as any).showInExport,
      extensionType: (obj as any).extensionType,
      fabricObject: obj,
      set(props: any) {
        const updates: any = {};
        if (props.x !== undefined) updates.left = props.x;
        if (props.left !== undefined) updates.left = props.left;
        if (props.y !== undefined) updates.top = props.y;
        if (props.top !== undefined) updates.top = props.top;
        if (props.width !== undefined) updates.scaleX = props.width / (obj.width || 1);
        if (props.height !== undefined) updates.scaleY = props.height / (obj.height || 1);
        if (props.fontSize !== undefined) updates.fontSize = props.fontSize;
        if (props.fill !== undefined) updates.fill = props.fill;
        if (props.opacity !== undefined) updates.opacity = props.opacity;
        if (props.angle !== undefined) updates.angle = props.angle;
        if (props.src !== undefined && obj.type === 'image') {
          (obj as fabric.Image).setSrc(props.src, () => {
            self._canvas?.requestRenderAll();
            self._onChanged();
          }, { crossOrigin: 'anonymous' });
          return;
        }
        obj.set(updates);
        self._canvas?.requestRenderAll();
        self._onChanged();
      },
    };
  }

  private _debouncedPushHistory() {
    if (this._historyTimer) clearTimeout(this._historyTimer);
    this._historyTimer = setTimeout(() => {
      this._historyTimer = null;
      this._pushHistory();
    }, 300);
  }

  private _scheduleThumbnailUpdate() {
    if (this._thumbTimer) clearTimeout(this._thumbTimer);
    this._thumbTimer = setTimeout(() => {
      this._thumbTimer = null;
      this._updateActiveThumbnail();
    }, 500);
  }

  private _pushHistory() {
    if (!this._canvas || this._suppressHistory) return;
    const json = this._canvas.toJSON(CUSTOM_PROPS);
    json.objects = (json.objects || []).filter(
      (o: any) => o.id !== '_page_bg' && o.id !== '_page_border'
    );
    this._historyStack = this._historyStack.slice(0, this._historyIndex + 1);
    this._historyStack.push(JSON.stringify(json));
    if (this._historyStack.length > this._maxHistory) this._historyStack.shift();
    this._historyIndex = this._historyStack.length - 1;
  }

  private _serializeActivePage() {
    if (!this._canvas || !this._pages[this._activePageIndex]) return;
    // Page content still loading — its stored JSON is still the truth, don't overwrite it with a partial canvas
    if ((this._canvas as any).__pageLoaded === false) return;
    const json = this._canvas.toJSON(CUSTOM_PROPS);
    json.objects = (json.objects || []).filter(
      (o: any) => o.id !== '_page_bg' && o.id !== '_page_border'
    );
    this._pages[this._activePageIndex].fabricJSON = json;
  }

  /** Renders every page's saved JSON into an offscreen canvas and caches its thumbnail.
   *  Called after loadJSON so the thumbnail strip shows content immediately on Edit. */
  private _generateAllThumbnails() {
    if (!this.width || !this.height || !this._pages.length) return;
    const w = this.width;
    const h = this.height;

    // Process pages one at a time (sequential) to avoid flooding the browser with canvases
    const processNext = (index: number) => {
      if (index >= this._pages.length) return;
      const page = this._pages[index];

      const fabricJSON = page.fabricJSON;
      if (!fabricJSON) {
        processNext(index + 1);
        return;
      }

      const offEl = document.createElement('canvas');
      const temp = new fabric.Canvas(offEl, { enableRetinaScaling: false });
      temp.setWidth(w);
      temp.setHeight(h);
      temp.loadFromJSON(fabricJSON, () => {
        (temp as any).backgroundColor = '#ffffff';
        const toHide: fabric.Object[] = [];
        temp.getObjects().forEach(o => {
          if ((o as any).showInExport === false) { (o as any).visible = false; toHide.push(o); }
        });
        temp.renderAll();

        // Only generate thumbnail if the page has visible content
        const visibleObjects = temp.getObjects().filter(o => (o as any).showInExport !== false);
        if (visibleObjects.length > 0) {
          const url = temp.toDataURL({ format: 'jpeg', quality: 0.5, multiplier: 0.2 });
          this._thumbnailCache[page.id] = url;
          runInAction(() => { this.thumbnails = { ...this._thumbnailCache }; });
        }
        toHide.forEach(o => { (o as any).visible = true; });
        temp.dispose();
        processNext(index + 1);
      });
    };

    processNext(0);
  }

  private _updateActiveThumbnail() {
    if (!this._pages[this._activePageIndex] || !this.width || !this._canvas) return;
    const pageId = this._pages[this._activePageIndex].id;
    const json = this._canvas.toJSON(CUSTOM_PROPS);

    const offEl = document.createElement('canvas');
    const temp = new fabric.Canvas(offEl, { enableRetinaScaling: false });
    temp.setWidth(this.width);
    temp.setHeight(this.height);
    temp.loadFromJSON(json, () => {
      (temp as any).backgroundColor = '#ffffff';
      const toHide: fabric.Object[] = [];
      temp.getObjects().forEach(o => {
        if ((o as any).showInExport === false) { (o as any).visible = false; toHide.push(o); }
      });
      temp.renderAll();
      const url = temp.toDataURL({ format: 'jpeg', quality: 0.5, multiplier: 0.2 });
      toHide.forEach(o => { (o as any).visible = true; });
      temp.dispose();
      this._thumbnailCache[pageId] = url;
      runInAction(() => { this.thumbnails = { ...this._thumbnailCache }; });
    });
  }

  /** Adds a white page rect + blue border as canvas objects at the bottom of the stack.
   *  These are regular Fabric objects so they follow the viewport transform (zoom/pan).
   *  They are excluded from serialization and re-added after every page load. */
  private _setPageBackground() {
    if (!this._canvas || !this.width || !this.height) return;
    const canvas = this._canvas;

    // Remove any stale background/border from a previous call or loaded JSON
    const stale = canvas.getObjects().filter(
      o => (o as any).id === '_page_bg' || (o as any).id === '_page_border'
    );
    stale.forEach(o => canvas.remove(o));

    // White page fill — visible in export too (intentional)
    const bg = new fabric.Rect({
      left: 0, top: 0,
      width: this.width, height: this.height,
      fill: '#ffffff',
      selectable: false, evented: false,
      lockMovementX: true, lockMovementY: true,
      lockScalingX: true, lockScalingY: true,
      lockRotation: true, hasControls: false, hasBorders: false,
    } as any);
    (bg as any).id = '_page_bg';
    (bg as any).customType = 'pageBg';

    // Blue border — editor-only visual guide, hidden in export/thumbnails
    const border = new fabric.Rect({
      left: 0, top: 0,
      width: this.width, height: this.height,
      fill: 'transparent',
      stroke: '#3b82f6', strokeWidth: 2,
      selectable: false, evented: false,
      lockMovementX: true, lockMovementY: true,
      lockScalingX: true, lockScalingY: true,
      lockRotation: true, hasControls: false, hasBorders: false,
    } as any);
    (border as any).id = '_page_border';
    (border as any).customType = 'pageBorder';
    (border as any).showInExport = false;

    canvas.insertAt(bg, 0, false);
    canvas.insertAt(border, 1, false);
  }

  private _loadActivePage() {
    if (!this._canvas) return;
    const snapshot = this._pages[this._activePageIndex];
    if (!snapshot) return;

    this._suppressHistory = true;
    this._historyStack = [];
    this._historyIndex = -1;

    const afterLoad = () => {
      this._setPageBackground();
      this._canvas!.requestRenderAll();
      this._suppressHistory = false;
      runInAction(() => { this.layersRevision++; });
      requestAnimationFrame(() => this.fitCanvasToContainer());
    };

    if (snapshot.fabricJSON) {
      this._loadIntoCanvas(snapshot.fabricJSON, afterLoad);
    } else if (snapshot.polotnoPage) {
      const canvas = this._canvas;
      const seq = this._startPageLoad(canvas, false);
      canvas.clear();
      this._suppressHistory = false;
      const children: any[] = snapshot.polotnoPage.children || [];
      const filtered = children.filter((c: any) => !(c.type === 'svg' && c.showInExport === false));
      // Elements that finish loading after the user has moved to another page are dropped
      const isCurrent = () => canvas === this._canvas && seq === (canvas as any).__loadSeq;
      const imageLoads = filtered.map((c: any) => this._addElement(c, isCurrent)).filter((r: any) => r instanceof Promise);
      afterLoad();
      // Images arrive asynchronously; only allow serializing this page once they're all on the canvas.
      // Each image promise always resolves (null on failure), so a broken image can't block this.
      Promise.all(imageLoads).then(() => {
        if (isCurrent()) (canvas as any).__pageLoaded = true;
      });
    } else {
      this._startPageLoad(this._canvas, true);
      afterLoad();
    }
  }

  /** `isCurrent`, when given, is checked once an async element has loaded; if it returns false the
   *  element is not added (the page it belonged to is no longer on the canvas). */
  private _addElement(el: any, isCurrent?: () => boolean): any {
    if (!this._canvas) return null;
    const canvas = this._canvas;

    const lockProps = el.selectable === false ? {
      lockMovementX: true, lockMovementY: true,
      lockScalingX: true, lockScalingY: true,
      lockRotation: true, hasControls: false, hasBorders: false,
    } : {};

    if (el.type === 'image') {
      return new Promise<fabric.Image | null>(resolve => {
        // Load the element ourselves rather than via fabric.Image.fromURL: on a failed load fromURL throws
        // inside Fabric (new fabric.Image(null)) and never calls back, so this promise would never settle.
        fabric.util.loadImage(el.src, (imgEl: HTMLImageElement | null, isError?: boolean) => {
          if (isError || !imgEl || (isCurrent && !isCurrent())) { resolve(null); return; }
          // crossOrigin on the object (as fromURL set it) is serialized, so reloaded images don't taint the canvas
          const img = new fabric.Image(imgEl, { crossOrigin: 'anonymous' } as any);
          const displayW = el.width ?? img.width ?? 100;
          const displayH = el.height ?? img.height ?? 100;
          img.set({
            left: el.x ?? el.left ?? 0,
            top: el.y ?? el.top ?? 0,
            scaleX: displayW / (img.width || displayW),
            scaleY: displayH / (img.height || displayH),
            selectable: el.selectable !== false,
            evented: el.selectable !== false,
            opacity: el.opacity ?? 1,
            angle: el.angle ?? 0,
            ...lockProps,
          } as any);
          (img as any).id = el.id || nanoid(6);
          if (el.customType) (img as any).customType = el.customType;
          if (el.extensionType) (img as any).extensionType = el.extensionType;
          if (el.showInExport !== undefined) (img as any).showInExport = el.showInExport;
          canvas.add(img);
          if (el.alwaysOnTop) canvas.bringToFront(img);
          canvas.requestRenderAll();
          resolve(img);
        }, null, 'anonymous');
      });
    }

    if (el.type === 'text') {
      const box = new fabric.Textbox(el.text || 'New Text', {
        left: el.x ?? el.left ?? 50,
        top: el.y ?? el.top ?? 50,
        fontSize: el.fontSize ?? 60,
        fill: el.fill ?? '#000000',
        fontFamily: el.fontFamily ?? 'Arial',
        fontWeight: el.fontWeight ?? 'normal',
        fontStyle: el.fontStyle ?? 'normal',
        underline: el.underline ?? false,
        textAlign: el.textAlign ?? 'left',
        width: el.width ?? 400,
        opacity: el.opacity ?? 1,
        angle: el.angle ?? 0,
      } as any);
      (box as any).id = el.id || nanoid(6);
      (box as any).customType = 'text';
      canvas.add(box);
      canvas.requestRenderAll();
      return box;
    }

    if (el.type === 'svg') {
      const svgSrc: string = el.src ?? '';
      let svgString = '';
      if (svgSrc.startsWith('data:image/svg+xml;base64,')) {
        try { svgString = atob(svgSrc.split(',')[1]); } catch (_) {}
      } else if (svgSrc.startsWith('data:image/svg+xml,')) {
        svgString = decodeURIComponent(svgSrc.split(',')[1]);
      } else if (svgSrc.startsWith('<svg')) {
        svgString = svgSrc;
      }
      if (!svgString) return null;

      fabric.loadSVGFromString(svgString, (objects, options) => {
        if (!objects.length || (isCurrent && !isCurrent())) return;
        const group = fabric.util.groupSVGElements(objects, options);
        const displayW = el.width ?? (group.width || 100);
        const displayH = el.height ?? (group.height || 100);
        group.set({
          left: el.x ?? el.left ?? 0,
          top: el.y ?? el.top ?? 0,
          scaleX: displayW / (group.width || displayW),
          scaleY: displayH / (group.height || displayH),
          selectable: false,
          evented: false,
          opacity: el.opacity ?? 0.5,
          ...lockProps,
        } as any);
        (group as any).id = el.id || nanoid(6);
        (group as any).customType = 'svg';
        (group as any).extensionType = el.extensionType;
        (group as any).showInExport = el.showInExport !== undefined ? el.showInExport : false;
        canvas.add(group);
        if (el.alwaysOnTop) canvas.bringToFront(group);
        canvas.requestRenderAll();
      });
      return null;
    }

    return null;
  }

  private _normalizeJSON(json: any): { pages: PageSnapshot[]; width: number; height: number } {
    if (!json) return { width: this.width, height: this.height, pages: [] };

    if (json._editorVersion) {
      return {
        width: json.width || this.width,
        height: json.height || this.height,
        pages: (json.pages || []).map((p: any) => ({
          id: p.id || nanoid(10),
          // A legacy page saved before it was ever opened carries polotnoPage instead of fabricJSON
          fabricJSON: p.fabricJSON || (p.polotnoPage ? null : { objects: [] }),
          polotnoPage: p.fabricJSON ? undefined : p.polotnoPage,
        })),
      };
    }

    // Legacy Polotno format
    if (json.pages && Array.isArray(json.pages) && json.pages[0]?.children !== undefined) {
      return {
        width: json.width || this.width,
        height: json.height || this.height,
        pages: json.pages.map((p: any) => ({
          id: p.id || nanoid(10),
          fabricJSON: null,
          polotnoPage: p,
        })),
      };
    }

    // Single Fabric canvas JSON
    if (json.objects !== undefined) {
      return {
        width: this.width,
        height: this.height,
        pages: [{ id: nanoid(10), fabricJSON: json }],
      };
    }

    return { width: this.width, height: this.height, pages: [] };
  }
}

const editorStore = new EditorStore();
export default editorStore;
